import * as moment from 'moment-timezone';
import { environment } from 'environments/environment';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { User } from 'app/shared/models/User';
import { Challenge, ChallengeService, Team, TeamMembershipStatus, TeamUser } from 'app/shared/services/challenge.service';
import { UserService } from 'app/shared/services/user.service';
import { Subject } from 'rxjs';

/**
 * Component to display tab for next challenge on statistics page
 */
@Component({
  selector: 'app-next-challenge',
  templateUrl: './next-challenge.component.html',
  styleUrls: ['./next-challenge.component.scss'],
})
export class NextChallengeComponent implements OnInit, OnDestroy {
  // The next challenge
  @Input() challenge: Challenge;

  // The team of the challenge, if the user is already member of a team or has sent an application to a team
  public team: Team;

  public inviteUserForm = new FormGroup({
    email: new FormControl('', [
      Validators.required,
      Validators.pattern(
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      ),
    ]),
  });

  // The currently logged in user
  public currentUser: User;

  // Team user object of the currently logged in user
  public loggedInUser: TeamUser;

  // Dialog displayed when user needs to confirm that he wants to leave team
  public displayLeaveTeamDialog: boolean = false;

  // Dialog displayed when founder needs to confirm that he wants to delete the team
  public displayDeleteTeamDialog: boolean = false;

  // Mailto email template for generating invitation mail
  public inviteMembersTemplate: string;

  // Whether team has been loaded from challenge service
  public teamLoaded = false;

  // Reference to TeamMembershipStatus to be able to use it in template
  public TeamMembershipStatus: typeof TeamMembershipStatus = TeamMembershipStatus;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private router: Router,
    private userService: UserService,
    private challengeService: ChallengeService,
    private translate: TranslateService,
    private snackBar: MatSnackBar,
  ) {}

  /**
   * Subscribe to logged in user, team of logged in user, and user object in team of logged in user
   */
  public ngOnInit(): void {
    this.userService.currentUser.takeUntil(this.ngUnsubscribe).subscribe((user) => {
      if (user) {
        this.currentUser = user;
      }
    });

    this.challengeService.team.takeUntil(this.ngUnsubscribe).subscribe((team) => {
      this.teamLoaded = true;
      this.team = team;
      this.generateInvitationMailTemplate();
    });

    this.challengeService.teamUser.takeUntil(this.ngUnsubscribe).subscribe((teamUser) => {
      this.loggedInUser = teamUser;
    });
  }

  public navigateSelectTeam(challengeID): void {
    this.router.navigate(['challenge', challengeID, 'team']).then();
  }

  public showLeaveTeamDialog() {
    this.displayLeaveTeamDialog = true;
    window.scrollTo(0, 0);
  }

  public leaveTeam(challengeID, teamID, userID) {
    this.challengeService.removeUserFromTeam(challengeID, teamID, userID).then();
    this.displayLeaveTeamDialog = false;
  }

  public showDeleteTeamDialog() {
    this.displayDeleteTeamDialog = true;
    window.scrollTo(0, 0);
  }

  public deleteTeam(challengeID, teamID) {
    this.challengeService.deleteTeam(challengeID, teamID).then();
    this.displayDeleteTeamDialog = false;
  }

  /**
   * Invites a user to the team
   */
  public inviteUserToTeam() {
    this.challengeService
      .inviteUsers(this.challenge._id, this.team._id, [this.inviteUserForm.value.email])
      .then(() => {
        this.inviteUserForm.reset();
      })
      .catch((error) => {
        const errorObject = error.body.error.error;

        if (errorObject.startsWith('invalid_domain')) {
          this.translate.get('CHALLENGE.error.invalidInvitationDomain').subscribe((text) => {
            this.snackBar.open(text, null, {
              verticalPosition: 'top',
              duration: 4000,
              panelClass: ['b2a-error', 'top'],
            });
          });
        } else if (errorObject.startsWith('maximum_challenge_members')){
          this.translate.get('CHALLENGE.error.maximumChallengeMemberReached').subscribe((text) => {
            this.snackBar.open(text, null, {
              verticalPosition: 'top',
              duration: 4000,
              panelClass: ['b2a-error', 'top'],
            });
          });  
        } else {
          this.translate.get('CHALLENGE.error.userInviteFailed').subscribe((text) => {
            this.snackBar.open(text, null, {
              verticalPosition: 'top',
              duration: 4000,
              panelClass: ['b2a-error', 'top'],
            });
          });

          this.challengeService.reloadTeams();
        }
      });
  }

  /**
   * Accept invitation to a team
   */
  public acceptInvitation(teamID, userID) {
    this.challengeService.acceptInvitation(this.challenge._id, teamID, userID).catch((error) => {
      const errorObject = error.body.error.error;

      if (errorObject.startsWith('maximum_challenge_members')){
        this.translate.get('CHALLENGE.error.maximumChallengeMemberReached').subscribe((text) => {
          this.snackBar.open(text, null, {
            verticalPosition: 'top',
            duration: 4000,
            panelClass: ['b2a-error', 'top'],
          });
        });
      } else {
        this.translate.get('CHALLENGE.error.general').subscribe((text) => {
          this.snackBar.open(text, null, {
            verticalPosition: 'top',
            duration: 4000,
            panelClass: ['b2a-error', 'top'],
          });
          this.challengeService.reloadTeams();
        });
      }
    });
  }

  /**
   * Cancels application of a user to the team
   */
  public denyApplication(userID) {
    this.challengeService.cancelApplication(this.challenge._id, this.team._id, userID).catch(() => {
      this.translate.get('CHALLENGE.error.general').subscribe((text) => {
        this.snackBar.open(text, null, {
          verticalPosition: 'top',
          duration: 4000,
          panelClass: ['b2a-error', 'top'],
        });
        this.challengeService.reloadTeams();
      });
    });
  }

  /**
   * Cancels an invitation of the user to the team
   */
  public cancelInvitation(teamID, userEmail) {
    this.challengeService.declineInvitation(this.challenge._id, teamID, userEmail).catch(() => {
      this.translate.get('CHALLENGE.error.general').subscribe((text) => {
        this.snackBar.open(text, null, {
          verticalPosition: 'top',
          duration: 4000,
          panelClass: ['b2a-error', 'top'],
        });
        this.challengeService.reloadTeams();
      });
    });
  }

  /**
   * On destroy cancel active subscriptions
   */
  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Team members get an option to send an email to invite company colleagues
   */
  public generateInvitationMailTemplate(): void {
    if (!this.team) {
      return;
    }

    let mailTemplate = `mailto:?subject=Einladung%20zur%20Back2Action%20Team%20Challenge&body=Hallo%2C%0A%0AIhr%20seid%20herzlich%20eingeladen%20an%20der%20Back2Action%20Team%20Challenge%20teilzunehmen%2C%20die%20am%20%3AstartDate%20startet.%0A%0AIhr%20k%C3%B6nnt%20zwischen%20folgenden%20Optionen%20w%C3%A4hlen%3A%0A%0A-%20Anfrage%20zur%20Mitgliedschaft%20bei%20meinem%20Team%20%E2%80%9E%3AteamName%22%0A-%20Anfrage%20bei%20einem%20anderen%20Team%0A-%20oder%20ein%20eigenes%20Team%20gr%C3%BCnden%0A%0AHinweis%3A%20Die%20Teamgr%C3%B6%C3%9Fe%20ist%20beschr%C3%A4nkt%20auf%20mind.%20%3AminMembers%20und%20max.%20%3AmaxMembers%20Mitglieder.%20Wenn%20das%20Team%20voll%20ist%2C%20k%C3%B6nnen%20gerne%20weitere%20Teams%20gegr%C3%BCndet%20werden.%0A%0AFalls%20ihr%20euch%20noch%20nicht%20bei%20Back2Action%20registriert%20habt%2C%20m%C3%BCsst%20ihr%20das%20vorab%20%C3%BCber%20den%20folgenden%20Link%20tun%3A%20%3AregistrationLink%0A%0AAuf%20geht%E2%80%99s%2C%20lasst%20uns%20durchstarten!%0A%0A`;

    mailTemplate = mailTemplate.replace(/%3AteamName/g, this.team.name);
    mailTemplate = mailTemplate.replace(/%3AstartDate/g, moment(this.challenge.start).format('DD.MM.YYYY'));
    mailTemplate = mailTemplate.replace(/%3AminMembers/g, this.challenge.minimalTeamMembers.toString());
    mailTemplate = mailTemplate.replace(/%3AmaxMembers/g, this.challenge.maximumTeamMembers.toString());

    // If active user belongs to a branch office, register for branch office. Else register for company.
    // Set selectTeam query param, so that user is redirected to team selection screen at first login after registration
    const registrationLink = `${window.location.origin}/signup/de/${this.currentUser.company.registrationToken}/`
      .concat(
        this.currentUser.branchoffice && this.currentUser.branchoffice.registrationToken
          ? this.currentUser.branchoffice.registrationToken
          : '',
      )
      .concat('?selectTeam=true');
    mailTemplate = mailTemplate.replace(/%3AregistrationLink/g, registrationLink);

    this.inviteMembersTemplate = mailTemplate;
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src1521087412/src/client/web-client/src/app/theme/components/challenge-statistics/components/next-challenge/next-challenge.component.ts